import React, { useState, useEffect } from "react";
import { FormFooter } from "@/components/FormFooter";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { cajaBancosAPI, type Banco } from "@/lib/api";
import { roundToPeso } from "@/lib/roundToPeso";

interface BancosABMProps {
  banco: Banco | null;
  onBack: () => void;
}

const tipoCuentaOptions = [
  { value: "CC_Pesos", label: "Cuenta Corriente Pesos" },
  { value: "CA_Pesos", label: "Caja de Ahorro Pesos" },
  { value: "CC_Dolares", label: "Cuenta Corriente Dólares" },
  { value: "CA_Dolares", label: "Caja de Ahorro Dólares" },
];

export function BancosABM({ banco, onBack }: BancosABMProps) {
  const [codigo, setCodigo] = useState("");
  const [nombreBanco, setNombreBanco] = useState("");
  const [fechaCreacion, setFechaCreacion] = useState("");
  const [saldoInicial, setSaldoInicial] = useState("");
  const [tipoCuenta, setTipoCuenta] = useState<string>("CC_Pesos");
  const [saldoActual, setSaldoActual] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (banco) {
      setCodigo(banco.codigo);
      setNombreBanco(banco.nombreBanco);
      setFechaCreacion(banco.fechaCreacion);
      setSaldoInicial(banco.saldoInicial.toString());
      setTipoCuenta(banco.tipoCuenta);
      setSaldoActual(banco.saldoActual);
    } else {
      setCodigo("");
      setFechaCreacion(new Date().toISOString().split('T')[0]);
      setSaldoActual(0);
    }
  }, [banco]);

  useEffect(() => {
    if (!banco) {
      const inicial = parseFloat(saldoInicial) || 0;
      setSaldoActual(inicial);
    }
  }, [saldoInicial, banco]);

  const handleSave = async () => {
    if (!nombreBanco.trim()) {
      toast.error("El nombre del banco es requerido");
      return;
    }

    if (!fechaCreacion) {
      toast.error("La fecha de creación es requerida");
      return;
    }

    setLoading(true);
    try {
      if (banco) {
        await cajaBancosAPI.updateBanco(banco.codigo, {
          nombreBanco,
          fechaCreacion,
          saldoInicial: roundToPeso(parseFloat(saldoInicial) || 0),
          tipoCuenta,
        });
        toast.success("Banco actualizado exitosamente");
      } else {
        await cajaBancosAPI.createBanco({
          codigo: "",
          nombreBanco,
          fechaCreacion,
          saldoInicial: roundToPeso(parseFloat(saldoInicial) || 0),
          tipoCuenta,
          moneda: tipoCuenta.includes('Dolar') ? 'USD' : 'ARS',
          activo: true,
        });
        toast.success("Banco creado exitosamente");
      }
      setHasChanges(false);
      setTimeout(() => onBack(), 500);
    } catch (error: any) {
      console.error("Error saving banco:", error);
      toast.error(error.message || "Error al guardar el banco");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await cajaBancosAPI.deleteBanco(codigo);
      toast.success("Banco eliminado exitosamente");
      setShowDeleteDialog(false);
      setTimeout(() => onBack(), 500);
    } catch (error: any) {
      console.error("Error deleting banco:", error);
      toast.error(error.message || "Error al eliminar el banco");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = () => {
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="codigo">Código</Label>
            <Input
              id="codigo"
              value={codigo}
              disabled
              tabIndex={-1}
              className="bg-muted"
              placeholder="Auto-generado"
            />
          </div>
          <div>
            <Label htmlFor="fechaCreacion">Fecha de Creación</Label>
            <DateInput
              id="fechaCreacion"
              value={fechaCreacion}
              onChange={(ymd) => {
                setFechaCreacion(ymd);
                handleInputChange();
              }}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="nombreBanco">Nombre del Banco</Label>
          <Input
            id="nombreBanco"
            value={nombreBanco}
            onChange={(e) => {
              setNombreBanco(e.target.value);
              handleInputChange();
            }}
            placeholder="Ej: Banco Galicia"
            tabIndex={2}
          />
        </div>

        <div>
          <Label>Tipo de Cuenta</Label>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {tipoCuentaOptions.map((option, index) => (
              <label
                key={option.value}
                className="flex items-center gap-2 p-3 border border-input rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <input
                  type="radio"
                  name="tipoCuenta"
                  value={option.value}
                  checked={tipoCuenta === option.value}
                  onChange={(e) => {
                    setTipoCuenta(e.target.value);
                    handleInputChange();
                  }}
                  tabIndex={3 + index}
                  className="w-4 h-4"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="saldoInicial">Saldo Inicial (Opcional)</Label>
            <NumberField
              id="saldoInicial"
              value={saldoInicial}
              onChange={(e) => {
                setSaldoInicial(e.target.value);
                handleInputChange();
              }}
              onBlur={() => {
                const rounded = String(roundToPeso(parseFloat(saldoInicial) || 0));
                if (rounded !== saldoInicial) {
                  setSaldoInicial(rounded);
                  handleInputChange();
                }
              }}
              placeholder="0.00"
              step="1"
              tabIndex={7}
            />
          </div>
          <div>
            <Label htmlFor="saldoActual">Saldo Actual</Label>
            <div
              className={`px-4 py-2 border border-input bg-muted rounded-lg text-sm font-semibold ${saldoActual < 0 ? 'text-red-400' : 'text-green-400'
                }`}
            >
              ${saldoActual.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      <FormFooter
        onBack={onBack}
        backDisabled={loading}
        backTabIndex={9}
        onDelete={banco ? () => setShowDeleteDialog(true) : undefined}
        deleteTabIndex={10}
        deleteDisabled={loading}
        onSave={handleSave}
        saveDisabled={!hasChanges || loading}
        saveTabIndex={8}
        saving={loading}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El banco será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} variant="destructive">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}